import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { cn } from '../primitives';

const ICONS_PER_PAGE = 96;

// Score color mapping (matching Rails Diaries::Field::COLOR_BY_SCORE)
export const SCORE_COLORS: Record<number, string> = {
  0: '#ff0000',
  12: '#ff4400',
  25: '#ff8800',
  37: '#ffBB00',
  50: '#fffb00',
  62: '#DDff00',
  75: '#BBff00',
  87: '#99ff00',
  100: '#00ff00',
};

export const SCORE_VALUES = [0, 12, 25, 37, 50, 62, 75, 87, 100] as const;

export interface Icon {
  key: string;
  name: string;
  category: string;
  path: string;
  style: 'default' | 'stroked';
}

export interface IconCategory {
  key: string;
  label: string;
}

export interface IconPickerLabels {
  searchPlaceholder?: string;
  categoryLabel?: string;
  styleLabel?: string;
  colorLabel?: string;
  defaultStyle?: string;
  strokedStyle?: string;
  allCategories?: string;
  noIconsFound?: string;
  loading?: string;
}

export interface IconPickerProps {
  selectedIcon?: string | null;
  selectedScore?: number;
  onSelect: (iconKey: string) => void;
  onScoreChange?: (score: number) => void;
  icons: Icon[];
  categories: IconCategory[];
  isLoading?: boolean;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  category?: string | null;
  onCategoryChange?: (category: string | null) => void;
  style?: 'default' | 'stroked';
  onStyleChange?: (style: 'default' | 'stroked') => void;
  className?: string;
  labels?: IconPickerLabels;
  renderIcon?: (icon: Icon, score: number) => React.ReactNode;
}

const defaultLabels: IconPickerLabels = {
  searchPlaceholder: 'Search icons...',
  categoryLabel: 'Category',
  styleLabel: 'Style',
  colorLabel: 'Color',
  defaultStyle: 'Default',
  strokedStyle: 'Stroked',
  allCategories: 'All',
  noIconsFound: 'No icons found',
  loading: 'Loading...',
};

export function IconPicker({
  selectedIcon,
  selectedScore = 100,
  onSelect,
  onScoreChange,
  icons,
  categories,
  isLoading = false,
  searchTerm = '',
  onSearchChange,
  category,
  onCategoryChange,
  style = 'default',
  onStyleChange,
  className,
  labels: customLabels,
  renderIcon,
}: IconPickerProps) {
  const labels = { ...defaultLabels, ...customLabels };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange?.(e.target.value);
    },
    [onSearchChange]
  );

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onCategoryChange?.(e.target.value || null);
    },
    [onCategoryChange]
  );

  const handleStyleChange = useCallback(
    (newStyle: 'default' | 'stroked') => {
      onStyleChange?.(newStyle);
    },
    [onStyleChange]
  );

  const currentColor = SCORE_COLORS[selectedScore] ?? SCORE_COLORS[100];

  // Default icon renderer - compact 40x40 size
  const defaultRenderIcon = useCallback(
    (icon: Icon, score: number) => (
      <div
        className="icon-picker__icon-circle"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: SCORE_COLORS[score] ?? SCORE_COLORS[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <i
          className={`icon-sm icon-${icon.key}`}
          style={{ width: 24, height: 24 }}
        />
      </div>
    ),
    []
  );

  const iconRenderer = renderIcon ?? defaultRenderIcon;

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(icons.length / ICONS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, category, style]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageStart = (currentPage - 1) * ICONS_PER_PAGE;
  const pageEnd = Math.min(pageStart + ICONS_PER_PAGE, icons.length);
  const visibleIcons = useMemo(
    () => icons.slice(pageStart, pageEnd),
    [icons, pageStart, pageEnd]
  );

  return (
    <div
      className={cn('icon-picker icon-picker--full', className)}
      style={{ display: 'block', width: '100%' }}
    >
      {/* Search Bar - compact */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ position: 'relative' }}>
          <svg
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 14,
              height: 14,
              color: '#6b7280',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={labels.searchPlaceholder}
            value={searchTerm}
            onChange={handleSearchChange}
            data-testid="icon-search-input"
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
            onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
          />
        </div>
      </div>

      {/* Compact Filter Bar - responsive */}
      <div
        className="icon-picker__filters"
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {/* Category Select */}
        <select
          value={category ?? ''}
          onChange={handleCategoryChange}
          data-testid="icon-category-selector"
          style={{
            padding: '5px 24px 5px 8px',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: 12,
            backgroundColor: '#fff',
            cursor: 'pointer',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 4px center',
            backgroundSize: '14px',
          }}
        >
          <option value="">{labels.allCategories}</option>
          {categories.map((cat) => (
            <option key={cat.key} value={cat.key}>
              {cat.label}
            </option>
          ))}
        </select>

        {/* Style Toggle */}
        <div
          style={{
            display: 'flex',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => handleStyleChange('default')}
            style={{
              padding: '5px 8px',
              fontSize: 12,
              border: 'none',
              cursor: 'pointer',
              backgroundColor: style === 'default' ? '#3b82f6' : '#fff',
              color: style === 'default' ? '#fff' : '#374151',
              transition: 'all 0.15s',
            }}
          >
            {labels.defaultStyle}
          </button>
          <button
            type="button"
            onClick={() => handleStyleChange('stroked')}
            style={{
              padding: '5px 8px',
              fontSize: 12,
              border: 'none',
              borderLeft: '1px solid #e5e7eb',
              cursor: 'pointer',
              backgroundColor: style === 'stroked' ? '#3b82f6' : '#fff',
              color: style === 'stroked' ? '#fff' : '#374151',
              transition: 'all 0.15s',
            }}
          >
            {labels.strokedStyle}
          </button>
        </div>

        {/* Color Palette - Horizontal, compact */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {SCORE_VALUES.map((score) => (
            <button
              key={score}
              type="button"
              onClick={() => onScoreChange?.(score)}
              title={`Score ${score}`}
              style={{
                width: 18,
                height: 18,
                borderRadius: 3,
                backgroundColor: SCORE_COLORS[score],
                border: selectedScore === score ? '2px solid #1f2937' : '1px solid rgba(0,0,0,0.15)',
                cursor: 'pointer',
                transition: 'transform 0.1s',
                transform: selectedScore === score ? 'scale(1.15)' : 'scale(1)',
                flexShrink: 0,
              }}
            />
          ))}
        </div>
      </div>

      {/* Icons Grid - paginated, fills available width */}
      <div
        className="icon-picker__grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
          gap: 6,
          minHeight: 380,
          padding: 8,
          backgroundColor: '#f9fafb',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
        }}
      >
        {isLoading ? (
          <div
            data-testid="icon-picker-loading"
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: 40,
              color: '#6b7280',
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                border: '2px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 8px',
              }}
            />
            {labels.loading}
          </div>
        ) : icons.length === 0 ? (
          <div
            data-testid="icon-picker-empty"
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: 40,
              color: '#6b7280',
            }}
          >
            {labels.noIconsFound}
          </div>
        ) : (
          visibleIcons.map((icon) => (
            <button
              key={icon.key}
              type="button"
              className={cn(
                'icon-picker__icon-item',
                selectedIcon === icon.key && 'icon-picker__icon-item--selected'
              )}
              onClick={() => onSelect(icon.key)}
              data-testid={`icon-item-${icon.key}`}
              title={icon.name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                border: selectedIcon === icon.key ? '2px solid #3b82f6' : '2px solid transparent',
                borderRadius: 6,
                backgroundColor: selectedIcon === icon.key ? '#eff6ff' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.1s',
                minHeight: 54,
              }}
            >
              {iconRenderer(icon, selectedScore)}
            </button>
          ))
        )}
      </div>

      {/* Pagination footer */}
      {!isLoading && icons.length > 0 && (
        <div
          className="icon-picker__pagination"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 10,
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          <span data-testid="icon-picker-count">
            {`Showing ${pageStart + 1}–${pageEnd} of ${icons.length}`}
          </span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="icon-picker-prev"
                className="icon-picker__page-btn"
                aria-label="Previous page"
              >
                ‹
              </button>
              <span style={{ padding: '0 8px', minWidth: 64, textAlign: 'center' }}>
                {`Page ${currentPage} / ${totalPages}`}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="icon-picker-next"
                className="icon-picker__page-btn"
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .icon-picker__icon-item:hover {
          background-color: #f3f4f6 !important;
        }
        .icon-picker__grid::-webkit-scrollbar {
          width: 6px;
        }
        .icon-picker__grid::-webkit-scrollbar-track {
          background: transparent;
        }
        .icon-picker__grid::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .icon-picker__grid::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .icon-picker--full {
          display: block !important;
          width: 100% !important;
          padding: 0 !important;
          margin: 0 !important;
          cursor: default !important;
        }
        .icon-picker__page-btn {
          width: 28px;
          height: 28px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #fff;
          color: #374151;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.1s;
        }
        .icon-picker__page-btn:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }
        .icon-picker__page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

export default IconPicker;
